import type { FC, ReactNode } from 'react';
import React, { useCallback, useMemo } from 'react';
import type { WebsiteHeaderLayout_PhotoBackground } from '@shared/types/WebsiteHeaderSettings.types';
import { Box, Flex, styled } from '@withjoy/joykit';
import { addRendition } from '@shared/utils/photoRendition';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import { SsrLazyScreen } from '@apps/guest/packages/layout-engine/components/SsrLazyScreen';
import { EventNameNavForStandaloneHeader } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import type { EventNameNavForStandaloneHeaderProps } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import type { EventPageType } from '@graphql/generated-base';
import { pxToRem } from '@withjoy/joykit/theme';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { GuestSiteEventDesign } from '../../../../layout.types';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { BouncingChevron } from '../components/BouncingChevron/BouncingChevron';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useTranslation } from '@shared/core';
import type { HorizontalAlignment } from '@shared/types/WebsiteHeaderSettings.types';
import { isInIframe } from '@shared/utils/isInIframe';
import { isInTemplatePreviewModal } from '@shared/utils/templatePreviewContext';
import { OptimizedEventGuestSitePhoto, LOW_RES_RENDITION } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';
import { PhotoBackgroundRenderer } from './PhotoBackgroundRenderer';
import { useIsCompactBrannanHeader } from '../hooks/useIsCompactBrannanHeader';

export type PhotoBackgroundLayoutProps = Readonly<
  {
    websiteHeader: WebsiteHeaderLayout_PhotoBackground;
    showSkeleton?: boolean;
    currentPageType: EventPageType | undefined;
    eventDesign: GuestSiteEventDesign;
    isInlineEditing?: boolean;
    renderedContainerHeight?: number;
    renderedContainerWidth?: number;
    editableContentRef?: React.RefObject<HTMLDivElement>;
    forceRenderViewIn?: 'mobile' | 'desktop';
    scaleValue?: number;
    placeholderImageUrl?: string;
    onTextAlignmentChange?: (alignment: HorizontalAlignment) => void;
  } & EventNameNavForStandaloneHeaderProps
>;

/* Sits above art sprites (docked+1) that live inside FrameOuter, which is a sibling
   rendered after StandaloneWebsiteHeader in the DOM. */
const PhotoBackgroundOuter = styled(Box)`
  z-index: ${({ theme }) => theme.zIndices.docked + 2};
`;

const CONTAINER_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.photoBackground.aspectRatio as number;
const CONTAINER_MOBILE_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.photoBackground.mobileAspectRatio as number;

const isPreviewing = isInIframe();
// True when this iframe is the WebsiteDesignerV2 TemplatePreviewDialog. The dialog
// intentionally inflates the iframe's intrinsic height beyond a normal viewport so
// users see more of the page; with `100svh` header height that inflation stretches
// the photoBackground header well past its 3:2 aspect ratio. When set, we drop
// height and let the existing `aspect-ratio: 1.5` constraint drive the height.
//
// Evaluated at module load (matches `isPreviewing` above) — the templatePreviewModal
// flag is fixed via the iframe URL at construction time and never changes for the
// lifetime of that iframe. SPA navigation within the guest cannot toggle this flag,
// which is the desired behavior: the modal context is set by the parent iframe URL,
// not by the guest's internal routing.
const isInTemplatePreviewModalContext = isInTemplatePreviewModal();

export const PhotoBackgroundLayout: FC<PhotoBackgroundLayoutProps> = props => {
  const {
    websiteHeader,
    showSkeleton,
    currentPageType,
    isInlineEditing = false,
    renderedContainerHeight,
    renderedContainerWidth,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    placeholderImageUrl,
    onTextAlignmentChange,
    pages,
    pageSlug,
    ...restProps
  } = props;

  const photo = websiteHeader.headerPhoto.photos?.[0];
  const { headerTitleStyle, navigationStyle } = websiteHeader.headerPhoto;

  const [containerDimensions, containerRef] = useSizeObserver();

  const [isMobile = false] = useResponsive({ values: { xxs: true, sm: false } });
  const [isBouncingChevronEnabled] = useIsFeatureEnabled('guestBackgroundHeaderBouncingChevronEnabled');
  // Breakpoint-dependent sizing must be CSS (responsive props), not `isMobile` JS: the JS value is
  // a UA guess on SSR that re-resolves at hydration, and a flip re-sizes the whole 100svh frame
  // (header-CLS audit S6). The numeric `isMobile` variant below stays for paint-level consumers
  // (renderer rendition math, nav-offset calc strings) that never affect document layout.
  const deviceSpecificContainerAspectRatio = useMemo(
    () => (photo?.mobileCropInfo ? { _: `${CONTAINER_MOBILE_ASPECT_RATIO}`, sm: `${CONTAINER_ASPECT_RATIO}` } : `${CONTAINER_ASPECT_RATIO}`),
    [photo?.mobileCropInfo],
  );
  const rendererContainerAspectRatio = isMobile && photo?.mobileCropInfo ? CONTAINER_MOBILE_ASPECT_RATIO : CONTAINER_ASPECT_RATIO;

  const { isCompactView } = useIsCompactBrannanHeader({
    pages,
    pageSlug,
    currentPageType,
    brannanNavigation: restProps.eventDesign?.brannanNavigation,
  });

  const shouldHidePhotoBackground = useMemo(() => {
    return isCompactView && restProps.navBarVariant === 'menu';
  }, [isCompactView, restProps.navBarVariant]);

  const { tq } = useTranslation('inlineEditor');

  const { height, aspectRatio } = useMemo(
    () =>
      isCompactView
        ? { height: '100%', aspectRatio: 'unset' }
        : {
            // Template-preview-modal: drop explicit height so `aspectRatio` drives the
            // size (1.5 = 3:2). Otherwise `100svh` would resolve against the dialog's
            // inflated iframe height and stretch the header far past its design intent.
            // The chevron's 90svh applies below `sm` via a responsive prop (CSS media query),
            // not `isMobile` — a hydration flip of the JS value re-sized the whole frame.
            height: isInTemplatePreviewModalContext
              ? 'auto'
              : isInlineEditing && renderedContainerHeight
                ? `${renderedContainerHeight}px`
                : isBouncingChevronEnabled
                  ? { _: '90svh', sm: '100svh' }
                  : '100svh',
            aspectRatio: deviceSpecificContainerAspectRatio,
          },
    [deviceSpecificContainerAspectRatio, isBouncingChevronEnabled, isCompactView, isInlineEditing, renderedContainerHeight],
  );

  // In modal-preview, the photoBackground frame is sized by `aspectRatio` rather than viewport
  // height. Mirror that here so EventNameNav's `calc(viewportHeightFallback - navBar - margins)`
  // calculations target the rendered frame instead of the inflated iframe viewport.
  const viewportHeightFallback = isInTemplatePreviewModalContext ? `calc(100vw / ${rendererContainerAspectRatio})` : isMobile && isBouncingChevronEnabled ? '90svh' : '100svh';

  const { containerAspectRatio, containerHeight, containerMinHeight, containerOverflow } = useMemo(() => {
    return {
      containerAspectRatio: shouldHidePhotoBackground ? 'unset' : aspectRatio,
      containerHeight: shouldHidePhotoBackground ? 'auto' : height,
      containerMinHeight: shouldHidePhotoBackground ? 'auto' : pxToRem(96),
      containerOverflow: shouldHidePhotoBackground || isInlineEditing ? 'visible' : 'hidden',
    };
  }, [shouldHidePhotoBackground, aspectRatio, height, isInlineEditing]);

  const handleEditTextClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editWebsiteHeaderCustomTitle',
        inlineEditData: { data: props, validationConstraints: { maxLength: 30 } },
      },
    });
  });

  const handleEditPhotoClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderPhoto' },
    });
  });

  const renderPhotoWrapper = useCallback(
    (photoElement: ReactNode) => (
      <InlineEditor
        elementLabel={tq`Photo`}
        wrapperType="actionInside"
        actionData={{ editPhoto: handleEditPhotoClick }}
        componentName="headerPhoto"
        pageName={currentPageType}
        pageSlug={pageSlug || ''}
        wrapperCSS={{ width: '100%', height: '100%' }}
        isEligibleForInlineEditing={!isCompactView}
      >
        {photoElement}
      </InlineEditor>
    ),
    [tq, handleEditPhotoClick, currentPageType, pageSlug, isCompactView],
  );

  const eventNameNavProps = {
    eventDisplayName: websiteHeader.eventDisplayName,
    colors: websiteHeader.headerPhoto.colors,
    headerTextData: websiteHeader.headerPhoto.eventName,
    headerTitleStyle,
    navigationStyle,
    isCompactView,
    currentPageType,
    handleEditTextClick,
    isContentEditable: isInlineEditing,
    editableContentRef,
    forceRenderViewIn,
    scaleValue,
    pages,
    pageSlug,
    renderedContainerHeight,
    renderedContainerWidth,
    viewportHeightFallback,
    onTextAlignmentChange,
    ...restProps,
  };

  return (
    <PhotoBackgroundOuter
      position="relative"
      width="100%"
    >
      <Flex
        ref={containerRef}
        position="relative"
        width="100%"
        aspectRatio={containerAspectRatio}
        height={containerHeight}
        minHeight={containerMinHeight}
        overflow={containerOverflow}
      >
        {shouldHidePhotoBackground ? (
          <EventNameNavForStandaloneHeader {...eventNameNavProps} />
        ) : (
          <SsrLazyScreen
            src={addRendition({ url: photo?.url || placeholderImageUrl, renditionSize: LOW_RES_RENDITION })}
            width="100%"
            height={height}
            aspectRatio={aspectRatio}
            skeletonGroupOverrides={{
              Root: {
                props: {
                  width: '100%',
                  height: height,
                  aspectRatio: aspectRatio,
                },
              },
              PlaceholderRoot: {
                props: {
                  width: '100%',
                  height: height,
                  aspectRatio: aspectRatio,
                  position: 'absolute',
                },
              },
            }}
            showSkeleton={showSkeleton}
          >
            {() => (
              <PhotoBackgroundRenderer
                photo={photo}
                placeholderImageUrl={placeholderImageUrl}
                containerDimensions={containerDimensions}
                // The renderer's flow-level box lives inside this component's CSS-sized frame
                // (overflow:hidden), so its plain-JS height can't shift the document.
                height="100%"
                isCompactView={isCompactView}
                renderedContainerHeight={renderedContainerHeight}
                renderedContainerWidth={renderedContainerWidth}
                isMobile={isMobile}
                deviceSpecificContainerAspectRatio={rendererContainerAspectRatio}
                photoRenderer={OptimizedEventGuestSitePhoto}
                renderPhotoWrapper={renderPhotoWrapper}
              >
                <Box
                  width="100%"
                  height="100%"
                  position={isCompactView ? 'relative' : 'absolute'}
                  zIndex="banner"
                  pointerEvents={isPreviewing ? 'none' : 'auto'}
                >
                  <EventNameNavForStandaloneHeader {...eventNameNavProps} />
                </Box>
              </PhotoBackgroundRenderer>
            )}
          </SsrLazyScreen>
        )}
      </Flex>
      {isBouncingChevronEnabled && isMobile && !isCompactView && !shouldHidePhotoBackground && !isInlineEditing && <BouncingChevron />}
    </PhotoBackgroundOuter>
  );
};

PhotoBackgroundLayout.displayName = 'PhotoBackgroundLayout';
